// web/src/theme/applyTheme/_color.ts —— 内部：本机存储键常量 + 行内属性登记 + 颜色工具（hexToRgb / alpha / mix / 对比度 / 描边取色）
// 拆自 web/src/theme/applyTheme.ts 的同名分区（内容原样搬运）；由 web/src/theme/applyTheme.ts 以 export * 聚合，导出面对调用方不变。

import { CHART_COLORS, CHART_PALETTE } from '../../utils/chartTheme';
import { syncPaletteColors } from '../palette';
import { BORDER_LEVELS, DEFAULT_BORDER_LEVEL, getBorderLevel, type BorderLevelDef, type BorderToneSpec } from '../borders';
import {
  DEFAULT_DENSITY,
  DEFAULT_LANDING,
  DEFAULT_MENU_EXPAND,
  DEFAULT_MOTION,
  DEFAULT_RADIUS,
  DEFAULT_TIME_FMT,
  DEFAULT_WIDTH,
  MOTION_MEDIA_QUERY,
  getDensityLevel,
  getLandingLevel,
  getMenuExpandLevel,
  getMotionLevel,
  getRadiusLevel,
  getTimeFmtLevel,
  getWidthLevel,
  resolveRadius,
  type DensityLevelDef,
  type RadiusLevelDef,
  type WidthLevelDef,
} from '../uiPrefs';
import { DEFAULT_THEME_KEY, getTheme, type ThemeDef } from '../themes';
export const STORAGE_KEY = 'bcc.theme';
export const BORDER_STORAGE_KEY = 'bcc.border';
export const WIDTH_STORAGE_KEY = 'bcc.width';
export const MOTION_STORAGE_KEY = 'bcc.motion';
export const RADIUS_STORAGE_KEY = 'bcc.radius';
export const DENSITY_STORAGE_KEY = 'bcc.density';
export const LANDING_STORAGE_KEY = 'bcc.landing';
export const MENU_EXPAND_STORAGE_KEY = 'bcc.menu.expand';
export const MENU_PINNED_STORAGE_KEY = 'bcc.menu.pinned';
export const TIME_FMT_STORAGE_KEY = 'bcc.timefmt';

/** 上一次写入的行内属性（键为 DOM 宿主 + 属性名的组合标识），用于切换时清理 */
export const applied = { root: new Set<string>(), body: new Set<string>() };

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** 主色三元组（`rgb(var(--bcc-primary-rgb))` 形态的 token 用） */
export const rgbTriplet = (hex: string) => hexToRgb(hex).join(', ');

/** 主色透明档（描边 / 微染底 / 光圈统一由此派生，避免每个主题手写一堆 rgba） */
export const alpha = (hex: string, a: number) => `rgba(${rgbTriplet(hex)}, ${a})`;

/** 与白色（或指定色）按比例混合，用于表格固定列实底等"需要不透明等效色"的场景 */
export function mix(hex: string, withHex: string, weight: number): string {
  const [r1, g1, b1] = hexToRgb(hex);
  const [r2, g2, b2] = hexToRgb(withHex);
  const to2 = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  return `#${to2(r1 * weight + r2 * (1 - weight))}${to2(g1 * weight + g2 * (1 - weight))}${to2(b1 * weight + b2 * (1 - weight))}`;
}

/** 主色的对比色（色相 +180°，明度固定在中高档）：供荧光笔等需要与主色拉开对比的强调底色使用。 */
export function complementHex(hex: string): string {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  const h2 = (h + 0.5) % 1;
  const s2 = Math.max(0.55, Math.min(0.9, s));
  const l2 = 0.58;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l2 < 0.5 ? l2 * (1 + s2) : l2 + s2 - l2 * s2;
  const p = 2 * l2 - q;
  const out = [hue2rgb(p, q, h2 + 1 / 3), hue2rgb(p, q, h2), hue2rgb(p, q, h2 - 1 / 3)]
    .map((v) => Math.round(v * 255).toString(16).padStart(2, '0'));
  return `#${out.join('')}`;
}

const relLum = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a: string, b: string): number => {
  const la = relLum(a);
  const lb = relLum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * 把中性文字色朝底色方向"提亮"到指定对比度，得到可交互控件的描边色。
 * 控件边界需要能被识别（≈3:1），直接用半透明浅档在浅底上不足 1.3:1 等于没有边界；
 * 由各主题自己的 text-2 派生可保留该主题的色温（冷/暖主题各得其所）。
 */
function borderTone(text2: string, target: number): string {
  // 权重 1 = 文字本色（最深）、0 = 纯白；对比度随权重单调递增，故取"刚好达标"的最小权重
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (contrast(mix(text2, '#ffffff', mid), '#ffffff') >= target) hi = mid;
    else lo = mid;
  }
  return mix(text2, '#ffffff', hi);
}

/** 按档位定义取控件描边色：alpha = 主色透明档（带主题色温）、contrast = 由中性文字色派生的实色档 */
export function toneOf(t: ThemeDef, spec: BorderToneSpec): string {
  return spec.kind === 'alpha' ? alpha(t.primary, spec.alpha) : borderTone(t.text2, spec.target);
}

